import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import EditorHeader from './header/header';
import EditorMainEventBanner from '../../../../general-ui/main-event-banner/main-event-banner';
import EditorSpeakers from './speakers/speakers';
import EditorAbout from './about/about';
import EditorProducts from './products/products';
import EditorVideo from './videos/videos';
import EditorSponsors from './sponsors/sponsors';
import EditorCallout from './callout/callout';
import EditorFooter from './footer/footer';
import { EPreviewTypes, LanguagesAbbr, PageModule, PageModuleType } from '../../../../../types/working-model';
import { EventsState } from '../../../../../store/types';
import BlankSection from './blank-section';
import classNames from 'classnames';
import { useParams } from 'react-router';
import { ParamsProps } from '../../../../live-event/live-event';
import EditorAgenda from './agenda-editor/agenda';
import './editor-main.scss';
import { resetScrollState } from '../../../../../store/actions/admin/event-panel';
import { getHomepageDefaultLanguage } from '../../../../../utils/get-language';
import useTranslationModules from '../../../../../i18n/useTranslationModules';
import LandingPreviewModal from './preview/eventPreview';
import { OptionalComponent } from '../../../../../utils/optional-component';
import { getTemplateClassName, isRtlLanguage, shouldDisplayLandingPage } from '../../../../../utils/utils';
import EventEditorHeader from './event-editor-header';
import ReadOnlyIndicator from '../../shared/read-only-indicator';
import { useReadonlyMode } from '../../shared/use-readonly-mode';
import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import Surveys from '../../session/editor/surveys/surveys';
import EditorAccordion from './accordion/editor-accordion';
import CookieBanner from '../../../../general-ui/cookie-banner/cookie-banner';
import CookieBannerStickyWrapper from '../../../../general-ui/cookie-banner/cookie-banner-sticky-wrapper';
interface ModuleMapType {
	[key: number]: any;
}
export interface BasicLangingModuleProps {
	page_module: PageModule;
	template: string;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr;
	isLandingPage?: boolean;
	isHomePage?: boolean;
	isCustomPage?: boolean;
	customPageId?: number;
}

export const Dummy: React.FC = () => <div></div>;

export const ModuleMap: ModuleMapType = {
	[PageModuleType.main_event_banner]: EditorMainEventBanner,
	[PageModuleType.speakers]: EditorSpeakers,
	[PageModuleType.agenda]: EditorAgenda,
	[PageModuleType.about]: EditorAbout,
	[PageModuleType.videos]: EditorVideo,
	[PageModuleType.callout]: EditorCallout,
	[PageModuleType.sponsors]: EditorSponsors,
	[PageModuleType.broadcast]: Dummy,
	[PageModuleType.questions]: Dummy,
	[PageModuleType.quizzes]: Dummy,
	[PageModuleType.survey]: Surveys,
	[PageModuleType.products]: EditorProducts,
	[PageModuleType.slideshows]: Dummy,
	[PageModuleType.documents]: Dummy,
	[PageModuleType.similar_sessions]: Dummy,
	[PageModuleType.feed]: Dummy,
	[PageModuleType.embed_widget]: BlankSection,
	[PageModuleType.description]: Dummy,
	[PageModuleType.blank]: BlankSection,
	[PageModuleType.accordion]: EditorAccordion,
};

const EditorMain: React.FC = () => {
	const {
		EventPanelReducer: {
			panelOpen,
			scrollToModule,
		},
		CreateEventReducer: {
			workingEvent,
			editorSize,
			previewMode,
			loadingEvent,
			blurEditor
		}
	} = useSelector((event: EventsState) => event);

	const dispatch = useDispatch();
	const translationUpdates = useTypedSelector(event => event.LiveEventReducer.translationUpdates);
	const refMap = useRef<any>({});
	const containerRef = useRef<HTMLDivElement | null>(null);
	const headerRef = useRef<HTMLDivElement | null>(null);

	const { language }: ParamsProps = useParams();

	const baseLanguage = getHomepageDefaultLanguage();
	const displayLandingPage = shouldDisplayLandingPage(workingEvent);
	const isReadOnly = useReadonlyMode();

	useTranslationModules({ language: language ?? baseLanguage });

	const handleScroll = useCallback(async (moduleId?: number, pageModuleType?: PageModuleType) => {
		let fullRef: any;
		// if there's a module id, scroll to that
		if (moduleId) {
			fullRef = refMap.current[moduleId];
		} else if (pageModuleType) {
			// else since we can't update already created modules because we never stored any module info, only the module type,
			// we will find the first module based on the type and scroll to that
			const matchedModule = workingEvent?.homepage?.modules?.find((module) => {
				return module.is_on && module.type === pageModuleType;
			});
			if (matchedModule?.id) {
				fullRef = refMap.current[matchedModule?.id];
			}
		}

		if (fullRef && containerRef?.current) {
			const headerHeight = headerRef?.current?.offsetHeight || 0;
			containerRef.current.scrollTop = fullRef.offsetTop - containerRef.current.offsetTop + headerHeight;
		}
	}, [workingEvent?.homepage?.modules]);

	useEffect(() => {
		if (scrollToModule !== null) {
			handleScroll(scrollToModule);
		}
		// after triggering the scroll, reset state incase
		// user clicks same module after scrolling
		setTimeout(() => dispatch(resetScrollState()), 2000);
	}, [dispatch, scrollToModule, handleScroll]);

	const modules = useMemo(() => {
		if (!workingEvent) return [];

		return workingEvent?.homepage?.modules?.map((module: PageModule) => {
			const Module = ModuleMap[module.type];

			// Never show survey module on landing page
			if (module.type === PageModuleType.survey) {
				return Dummy;
			}

			// handleScroll prop because sponsors and Main Event Banner
			// have call to action buttons that can scroll to Homepage sections
			return <Module
				key={module.id}
				page_module={module}
				template={getTemplateClassName(workingEvent.template.name)}
				ref={(ref: any) => refMap.current[module.id || 0] = ref}
				handleScroll={handleScroll}
				language={language}
				baseLanguage={baseLanguage}
				isLandingPage
			/>;
		});

		// exception added for translationUpdates, we want to ensure this re-renders if the translations are refreshed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workingEvent, handleScroll, language, baseLanguage, translationUpdates]);

	if (!workingEvent) return null;

	const template = workingEvent?.template?.name?.toLocaleLowerCase().replace(/\s/g, '') ?? '';

	return (
		<div className={classNames("editor-main-border", { 'force-rtl': isRtlLanguage(language) })}>
			<EventEditorHeader workingEvent={workingEvent} />
			<OptionalComponent display={displayLandingPage}>
				<>
					<div
						className={
							classNames(
								"editor editor-main-container",
								{
									'panel-open': !!panelOpen,
									loading: loadingEvent && blurEditor,
									'read-only': isReadOnly,
								},
								editorSize,
								template
							)
						}
						ref={containerRef}
					>
						<EditorHeader
							ref={headerRef}
							handleScroll={handleScroll}
							template={getTemplateClassName(workingEvent.template.name)}
							language={language}
							baseLanguage={baseLanguage}
						/>
						{modules}
						<EditorFooter
							handleScroll={handleScroll}
							template={getTemplateClassName(workingEvent.template.name)}
							baseLanguage={baseLanguage}
							language={language}
						/>
						<LandingPreviewModal open={previewMode === EPreviewTypes.Landing} />

						<CookieBannerStickyWrapper>
							<CookieBanner isEditor forceShowBanner={!!workingEvent?.settings.gdpr?.enable_cookie_banner} />
						</CookieBannerStickyWrapper>
					</div>
				</>
			</OptionalComponent>
			<ReadOnlyIndicator
				isOn={isReadOnly}
				defaultLanguage={workingEvent.settings.i18n?.base}
			/>
		</div>
	);
};

export default EditorMain;
